import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import {
  Card,
  Button,
  Tabs,
  Table,
  Drawer,
  Alert,
  Form,
  Input,
  Radio,
  DatePicker,
  Select,
  message,
  Modal,
  Spin,
  Descriptions,
  Space,
  Typography,
  Tag,
  Empty,
  Popover,
} from 'antd';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import {
  list,
  benefitListV2,
  benefitDeleteV2,
  detail,
  rewardList,
  create,
  update,
  queryActivityDeliveryLink,
  createActivityDeliveryLink,
  updateActivityDeliveryLink,
} from '@/api/activity/activity';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import { state } from '@/store/index';
import PetProfileV2Drawer from './components/PetProfileV2Drawer';
import { copyToClipboard } from '@/utils/utils';
import popupButtonTextImage from '@/assets/images/popupButtonText.png';

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;
const QR_LABEL_WIDTH = 140;
const QR_DESCRIPTION_LABEL_STYLE: React.CSSProperties = { width: QR_LABEL_WIDTH };
const QR_QUERY_RETRY_DELAY = 600;
const QR_QUERY_RETRY_TIMES = 2;
const INPUT_COUNT_PROPS = {
  formatter: ({ count, maxLength }: { count: number; maxLength?: number }) => `${count}/${maxLength}`,
};
const POPUP_BUTTON_TEXT_TIP = (
  <img
    src={popupButtonTextImage}
    alt="弹窗按钮文案示意图"
    style={{ width: 260, display: 'block' }}
  />
);
const POPUP_BUTTON_TEXT_LABEL = (
  <Space size={4}>
    <span>弹窗按钮文案</span>
    <Popover
      content={POPUP_BUTTON_TEXT_TIP}
      placement="right">
      <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'pointer' }} />
    </Popover>
  </Space>
);
const ACTIVITY_TABS = {
  PET_PROFILE: 'pet_profile',
  PET_PROFILE_V2: 'pet_profile_v2',
} as const;
const SHOP_APP_ID_MAP: Record<string, string> = {
  '36777487': '3000000140741384',
  '295760971': '3000000141512913',
  '210259195': '3000000143064021',
  '105018077': '3000000143150804',
  '215035496': '3000000143467565',
};
const JD_MINI_APP_ID = 'F2B9EFEFFD31D4B6FE00C462290C0001';
const JD_SHOP_CONFIG_MAP: Record<string, { dietType: number; memberCenterUrl?: string }> = {
  '650686': {
    dietType: 1,
    memberCenterUrl:
      'https://shop.m.jd.com/member/card?venderId=655156&shopId=650686&channel=80410853&userInfoEdit=true',
  },
  '734259': {
    dietType: 1,
    memberCenterUrl: 'https://shop.m.jd.com/member/card?venderId=739130&shopId=734259',
  },
  '1000001706': {
    dietType: 1,
    memberCenterUrl: 'https://shop.m.jd.com/member/card?venderId=1000001706&shopId=1000001706',
  },
};
// 天猫和京东开放 V2，微信和抖音不开放。
const PET_PROFILE_V2_SHOP_IDS = [
  '650686',
  '12782931',
  '105018077',
  '210259195',
  '215035496',
  '295760971',
  '1000001706',
];
const DELIVERY_CHANNELS = [
  { name: '首页banner', code: 'archive-home' },
  { name: '详情页banner', code: 'archive-detail' },
  { name: '会员页banner', code: 'archive-member' },
  { name: '客服链接', code: 'archive-customer' },
  { name: '短信', code: 'archive-sms' },
  { name: '首页弹窗', code: 'archive-home-popup' },
  { name: '直播间', code: 'archive-live' },
  { name: 'DM单', code: 'archive-dm' },
  { name: '会员咨询', code: 'archive-consult' },
  { name: '备用1', code: 'archive-backup-1' },
  { name: '备用2', code: 'archive-backup-2' },
];
const EXPIRE_ACTION_OPTIONS = [
  {
    label: '展示统一失效页',
    value: 'invalid-page',
    expireAction: 1,
    invalidAction: 'none',
  },
  {
    label: '5 秒后跳店铺首页',
    value: 'shop-home',
    expireAction: 2,
    invalidAction: 'shopHome',
  },
  {
    label: '5 秒后跳会员中心',
    value: 'member-center',
    expireAction: 2,
    invalidAction: 'memberCenter',
  },
];

const DateType: React.FC<any> = ({ value = {}, onChange }) => {
  const [type, setType] = useState('');
  const [time, setTime] = useState([]);

  const triggerChange = (changedValue: { type?: string; time?: any }) => {
    onChange?.({ type, time, ...value, ...changedValue });
  };

  return (
    <>
      <Radio.Group
        value={value.type || type}
        disabled={value.drawerType == 2 || value.drawerType === 3}
        onChange={e => {
          console.log(e.target.value);
          setType(e.target.value);
          triggerChange({ type: e.target.value });
        }}>
        <Radio value={'permanent'}>长期有效</Radio>
        <Radio value={'specified'}>指定时间</Radio>
      </Radio.Group>
      <RangePicker
        showTime
        value={value.time || time}
        disabled={value.drawerType == 2 || value.drawerType === 3}
        onChange={(dates: any) => {
          setTime(dates);
          triggerChange({ time: dates });
        }}
      />
    </>
  );
};

const renderActivityTimeRange = (record: any) => {
  if (record?.timeType === 'permanent') {
    return record?.timeTypeName || '长期有效';
  }
  if (record?.timeType === 'specified') {
    if (record?.startTime && record?.endTime) {
      return `${record.startTime}至${record.endTime}`;
    }
    return record?.timeTypeName || '--';
  }
  if (record?.startTime && record?.endTime) {
    return `${record.startTime}至${record.endTime}`;
  }
  return record?.timeTypeName || '长期有效';
};

const getExpireActionValue = (url?: string | null) => {
  if (!url) {
    return EXPIRE_ACTION_OPTIONS[0].value;
  }
  try {
    const searchParams = new URL(url).searchParams;
    const query = searchParams.get('query') || searchParams.get('path') || '';
    const invalidAction = new URLSearchParams(query.includes('?') ? query.split('?')[1] : query).get('invalidAction');
    return (
      EXPIRE_ACTION_OPTIONS.find(item => item.invalidAction === invalidAction)?.value || EXPIRE_ACTION_OPTIONS[0].value
    );
  } catch (error) {
    return EXPIRE_ACTION_OPTIONS[0].value;
  }
};

const getExpireActionOption = (value?: string) => {
  return EXPIRE_ACTION_OPTIONS.find(item => item.value === value) || EXPIRE_ACTION_OPTIONS[0];
};

const getAppIdFromUrl = (url?: string | null) => {
  if (!url) {
    return '';
  }
  try {
    return new URL(url).searchParams.get('_ariver_appid') || '';
  } catch (error) {
    return '';
  }
};

const isJdDeliveryUrl = (url?: string | null) => {
  return !!url && url.includes('mini-app-static.jd.com/apps/mpshare/index.html');
};

const buildExpireRedirectUrl = (appId: string, invalidAction: string) => {
  return `https://m.duanqu.com/?_ariver_appid=${appId}&query=${encodeURIComponent(
    `page=invalid-page&invalidAction=${invalidAction}`,
  )}`;
};

const buildJdMiniProgramUrl = ({
  path,
  shopId,
  dietType,
  channelNumber,
  extraParams,
}: {
  path: string;
  shopId: string | number;
  dietType: number;
  channelNumber?: string;
  extraParams?: Record<string, string | number | undefined | null>;
}) => {
  const params = new URLSearchParams({
    path,
    shopId: String(shopId),
    dietType: String(dietType),
  });
  if (channelNumber) {
    params.set('channelNumber', channelNumber);
  }
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return `https://mini-app-static.jd.com/apps/mpshare/index.html?category=jump&des=jdmp&vapptype=1&appId=${JD_MINI_APP_ID}&path=pages%2Findex%2Findex.html%3F${encodeURIComponent(
    params.toString(),
  )}`;
};

const getJdMiniProgramPageName = (pageName: string) => {
  const pageNameMap: Record<string, string> = {
    'first-archive-gift-v2': 'firstArchiveGiftV2',
  };
  return pageNameMap[pageName] || pageName;
};

const buildJdExpireRedirectUrl = (sourceUrl: string, shopId?: string | number | null, invalidAction?: string) => {
  const shopKey = String(shopId || '');
  const shopConfig = JD_SHOP_CONFIG_MAP[shopKey];
  if (!shopConfig) {
    return sourceUrl;
  }
  return buildJdMiniProgramUrl({
    path: 'invalidPage',
    shopId: shopKey,
    dietType: shopConfig.dietType,
    extraParams: {
      invalidAction,
    },
  });
};

const buildExpireRedirectUrlBySource = (sourceUrl: string, invalidAction: string, shopId?: string | number | null) => {
  const appId = getAppIdFromUrl(sourceUrl) || SHOP_APP_ID_MAP[String(shopId || '')];
  if (appId) {
    return buildExpireRedirectUrl(appId, invalidAction);
  }
  if (isJdDeliveryUrl(sourceUrl)) {
    return buildJdExpireRedirectUrl(sourceUrl, shopId, invalidAction);
  }
  return '';
};

const wait = (time: number) => new Promise(resolve => setTimeout(resolve, time));

const getQrCodeImageSrc = (detail?: any) => {
  const qrCodeBase64 = detail?.qrCodeBase64?.trim();
  if (qrCodeBase64) {
    return qrCodeBase64.startsWith('data:') ? qrCodeBase64 : `data:image/png;base64,${qrCodeBase64}`;
  }
  return undefined;
};

const ActivityList = () => {
  const navigate = useNavigate();

  const [editActivityId, setEditActivityId] = useState<string>('');

  const [data, setData] = useState([] as any);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [editor, setEditor] = useState<any>(null);
  const toolbarConfig = useMemo(() => defaultToolbarConfig, []);
  const [editorHtml, setEditorHtml] = useState<string>('');
  const [drawerType, setDrawerType] = useState(1);
  const [coupon, setCoupon] = useState([]);
  const [page, setPage] = useState({ pageSize: 10, pageNum: 1, total: 0 });
  const [activeTab, setActiveTab] = useState<(typeof ACTIVITY_TABS)[keyof typeof ACTIVITY_TABS]>(
    ACTIVITY_TABS.PET_PROFILE,
  );
  const [listRefreshVersion, setListRefreshVersion] = useState(0);
  const [v2DrawerOpen, setV2DrawerOpen] = useState(false);
  const [v2DrawerMode, setV2DrawerMode] = useState<'create' | 'view' | 'edit'>('create');
  const [v2ActivityId, setV2ActivityId] = useState<string | number>('');
  const [v2CopyFromActivityId, setV2CopyFromActivityId] = useState<string | number>('');
  const [deliveryLinkModalOpen, setDeliveryLinkModalOpen] = useState(false);
  const [deliveryLinks, setDeliveryLinks] = useState<{ name: string; code: string; url: string }[]>([]);
  const [deliveryLinkActivity, setDeliveryLinkActivity] = useState<any>(null);
  const [qrDrawerOpen, setQrDrawerOpen] = useState(false);
  const [qrDrawerLoading, setQrDrawerLoading] = useState(false);
  const [qrSaving, setQrSaving] = useState(false);
  const [qrDetail, setQrDetail] = useState<any>(null);
  const [qrChannel, setQrChannel] = useState<any>(null);
  const [qrForm] = Form.useForm();
  const qrExpireType = Form.useWatch('expireType', qrForm);
  const qrCodeImageSrc = getQrCodeImageSrc(qrDetail);
  const { currentShopPlatformType, currentShopId } = useSnapshot(state);
  const isPetProfileV1Visible = currentShopPlatformType === 'TM';
  const isPetProfileV2Visible = PET_PROFILE_V2_SHOP_IDS.includes(String(currentShopId || ''));
  const defaultActivityTab = isPetProfileV1Visible ? ACTIVITY_TABS.PET_PROFILE : ACTIVITY_TABS.PET_PROFILE_V2;

  // 添加一个状态来跟踪编辑器是否已准备好
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    if (editorReady && editorHtml) {
      // 编辑器准备好了，可以设置内容
      if (editor) {
        editor.setHtml(editorHtml);
      }
    }
  }, [editorReady, editorHtml, editor]);

  useEffect(() => {
    getList();
  }, [
    page.pageSize,
    page.pageNum,
    currentShopId,
    activeTab,
    listRefreshVersion,
    defaultActivityTab,
    isPetProfileV1Visible,
    isPetProfileV2Visible,
  ]);

  useEffect(() => {
    if (!isPetProfileV1Visible && activeTab === ACTIVITY_TABS.PET_PROFILE) {
      setActiveTab(defaultActivityTab);
      setOpen(false);
    }
    if (!isPetProfileV2Visible && activeTab === ACTIVITY_TABS.PET_PROFILE_V2) {
      setActiveTab(defaultActivityTab);
      setV2DrawerOpen(false);
    }
  }, [activeTab, defaultActivityTab, isPetProfileV1Visible, isPetProfileV2Visible]);

  useEffect(() => {
    return () => {
      if (editor && editor.destroy) {
        editor.destroy();
        setEditor(null);
      }
    };
  }, [editor]);

  const getList = async () => {
    if (!currentShopId) {
      setData([]);
      setPage(prev => ({
        ...prev,
        total: 0,
      }));
      return;
    }
    try {
      const params = { pageNum: page.pageNum, pageSize: page.pageSize, shopId: currentShopId };
      const requestTab =
        (activeTab === ACTIVITY_TABS.PET_PROFILE && isPetProfileV1Visible) ||
        (activeTab === ACTIVITY_TABS.PET_PROFILE_V2 && isPetProfileV2Visible)
          ? activeTab
          : defaultActivityTab;
      if (
        (requestTab === ACTIVITY_TABS.PET_PROFILE && !isPetProfileV1Visible) ||
        (requestTab === ACTIVITY_TABS.PET_PROFILE_V2 && !isPetProfileV2Visible)
      ) {
        setData([]);
        setPage(prev => ({
          ...prev,
          total: 0,
        }));
        return;
      }
      const res =
        requestTab === ACTIVITY_TABS.PET_PROFILE_V2
          ? await benefitListV2({ ...params, activityType: ACTIVITY_TABS.PET_PROFILE_V2 })
          : await list({ ...params, activityType: ACTIVITY_TABS.PET_PROFILE });
      setData(res?.rows || []);
      setPage(prev => ({
        ...prev,
        total: res?.total || 0,
      }));
      console.log(res);
    } catch (error) {
      console.error(error);
      setData([]);
      setPage(prev => ({
        ...prev,
        total: 0,
      }));
    }
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    form.resetFields(); // 重置表单字段
    setEditorHtml(''); // 清空编辑器内容
    if (editor) {
      editor.setHtml(''); // 清空编辑器实例内容
    }
    setOpen(false);
  };
  const onChange = (key: string) => {
    const nextKey = key as (typeof ACTIVITY_TABS)[keyof typeof ACTIVITY_TABS];
    setActiveTab(nextKey);
    setPage(prev => ({
      ...prev,
      pageNum: 1,
    }));
    console.log(key);
  };

  const openPetProfileV2Drawer = (mode: 'create' | 'view' | 'edit', record?: any) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    if (!isPetProfileV2Visible) {
      return;
    }
    setActiveTab(ACTIVITY_TABS.PET_PROFILE_V2);
    setV2DrawerMode(mode);
    setV2ActivityId(record?.activityId || '');
    setV2CopyFromActivityId('');
    setV2DrawerOpen(true);
  };

  const copyPetProfileV2Activity = (record: any) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    if (!isPetProfileV2Visible) {
      return;
    }
    if (!record?.activityId) {
      message.warning('缺少活动ID');
      return;
    }
    setActiveTab(ACTIVITY_TABS.PET_PROFILE_V2);
    setV2DrawerMode('create');
    setV2ActivityId('');
    setV2CopyFromActivityId(record.activityId);
    setV2DrawerOpen(true);
  };

  const closePetProfileV2Drawer = () => {
    setV2DrawerOpen(false);
    setV2ActivityId('');
    setV2CopyFromActivityId('');
    setV2DrawerMode('create');
  };

  const handleDeletePetProfileV2 = (record: any) => {
    Modal.confirm({
      title: '确认删除该活动？',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res: any = await benefitDeleteV2({ activityId: record.activityId });
          if (res?.code === 200) {
            message.success('删除成功');
            if (data.length === 1 && page.pageNum > 1) {
              setPage(prev => ({
                ...prev,
                pageNum: prev.pageNum - 1,
              }));
            } else {
              setListRefreshVersion(prev => prev + 1);
            }
          }
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const buildDeliveryLinks = (pageName: string, shopId?: string | number | null, platformType?: string | null) => {
    const shopKey = String(shopId || '');
    if (platformType === 'JD') {
      const shopConfig = JD_SHOP_CONFIG_MAP[shopKey];
      if (!shopConfig) {
        return [];
      }
      return DELIVERY_CHANNELS.map(item => ({
        ...item,
        url: buildJdMiniProgramUrl({
          path: getJdMiniProgramPageName(pageName),
          shopId: shopKey,
          dietType: shopConfig.dietType,
          channelNumber: item.code,
        }),
      }));
    }

    const appId = SHOP_APP_ID_MAP[shopKey];
    if (!appId) {
      return [];
    }
    return DELIVERY_CHANNELS.map(item => ({
      ...item,
      url: `https://m.duanqu.com/?_ariver_appid=${appId}&query=${encodeURIComponent(
        `page=${pageName}&channel=${item.code}`,
      )}`,
    }));
  };

  const openDeliveryLinkModal = (pageName: string, record: any) => {
    const targetShopId = record?.shopId || currentShopId;
    const targetPlatformType = record?.platformType || currentShopPlatformType;
    if (!targetShopId) {
      message.warning('缺少店铺信息');
      return;
    }
    const links = buildDeliveryLinks(pageName, targetShopId, targetPlatformType);
    if (!links.length) {
      message.warning('当前店铺暂未配置投放链接');
      return;
    }
    setDeliveryLinks(links);
    setDeliveryLinkActivity(record);
    setDeliveryLinkModalOpen(true);
  };

  const handleCopyDeliveryLink = async (url: string) => {
    try {
      await copyToClipboard(url);
      message.success('复制成功');
    } catch (error) {
      console.error(error);
      message.error('复制失败');
    }
  };

  const queryDeliveryLinkDetail = async (activityId: string | number, channelCode: string) => {
    const res: any = await queryActivityDeliveryLink({ activityId, channelCode });
    return res?.data || null;
  };

  const queryDeliveryLinkDetailWithRetry = async (activityId: string | number, channelCode: string) => {
    for (let index = 0; index <= QR_QUERY_RETRY_TIMES; index++) {
      const detailData = await queryDeliveryLinkDetail(activityId, channelCode);
      if (detailData) {
        return detailData;
      }
      if (index < QR_QUERY_RETRY_TIMES) {
        await wait(QR_QUERY_RETRY_DELAY);
      }
    }
    return null;
  };

  const fillQrForm = (detailData: any) => {
    const nextExpireType = detailData?.expireType || 1;
    qrForm.setFieldsValue({
      expireType: nextExpireType,
      effectiveTime:
        nextExpireType === 2 && detailData?.effectiveStartTime && detailData?.effectiveEndTime
          ? [dayjs(detailData.effectiveStartTime), dayjs(detailData.effectiveEndTime)]
          : undefined,
      expireActionValue: getExpireActionValue(detailData?.expireRedirectUrl),
    });
  };

  const openQrDrawer = async (channel: any) => {
    if (!deliveryLinkActivity?.activityId) {
      message.warning('缺少活动信息');
      return;
    }
    setQrChannel(channel);
    setQrDrawerOpen(true);
    setQrDrawerLoading(true);
    setQrDetail(null);
    qrForm.resetFields();

    try {
      let detailData = await queryDeliveryLinkDetail(deliveryLinkActivity.activityId, channel.code);
      if (!detailData) {
        const expireRedirectUrl = buildExpireRedirectUrlBySource(
          channel.url,
          EXPIRE_ACTION_OPTIONS[0].invalidAction,
          deliveryLinkActivity.shopId || currentShopId,
        );
        if (!expireRedirectUrl) {
          message.warning('缺少店铺跳转配置，无法生成二维码');
          return;
        }
        const createRes: any = await createActivityDeliveryLink({
          activityId: deliveryLinkActivity.activityId,
          channelCode: channel.code,
          channelName: channel.name,
          sourceUrl: channel.url,
          shopId: deliveryLinkActivity.shopId || currentShopId,
          platform: deliveryLinkActivity.platformType || currentShopPlatformType,
          expireRedirectUrl,
        });
        if (createRes?.code !== 200) {
          message.error(createRes?.msg || '二维码生成失败');
          return;
        }
        detailData = await queryDeliveryLinkDetailWithRetry(deliveryLinkActivity.activityId, channel.code);
      }
      if (!detailData) {
        message.error('二维码详情查询为空，请稍后重试');
        return;
      }
      setQrDetail(detailData);
      fillQrForm(detailData);
    } catch (error) {
      console.error(error);
      message.error('二维码信息获取失败');
    } finally {
      setQrDrawerLoading(false);
    }
  };

  const closeQrDrawer = () => {
    setQrDrawerOpen(false);
    setQrChannel(null);
    setQrDetail(null);
    qrForm.resetFields();
  };

  const handleSaveQrConfig = async () => {
    if (!qrDetail?.id || !qrChannel) {
      return;
    }
    try {
      const values = await qrForm.validateFields();
      const expireActionOption = getExpireActionOption(values.expireActionValue);
      const expireRedirectUrl = buildExpireRedirectUrlBySource(
        qrChannel.url,
        expireActionOption.invalidAction,
        deliveryLinkActivity?.shopId || currentShopId,
      );
      if (!expireRedirectUrl) {
        message.warning('缺少店铺跳转配置，无法保存二维码配置');
        return;
      }
      const params: any = {
        id: qrDetail.id,
        channelName: qrChannel.name,
        sourceUrl: qrChannel.url,
        expireType: values.expireType,
        expireAction: expireActionOption.expireAction,
        expireRedirectUrl,
        remark: qrDetail.remark || '活动投放链接有效期配置',
      };
      if (values.expireType === 2) {
        params.effectiveStartTime = values.effectiveTime?.[0]?.format('YYYY-MM-DD HH:mm:ss');
        params.effectiveEndTime = values.effectiveTime?.[1]?.format('YYYY-MM-DD HH:mm:ss');
      }
      setQrSaving(true);
      const res: any = await updateActivityDeliveryLink(params);
      if (res?.code === 200) {
        message.success('保存成功');
        const detailData = await queryDeliveryLinkDetail(deliveryLinkActivity.activityId, qrChannel.code);
        setQrDetail(detailData);
        fillQrForm(detailData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setQrSaving(false);
    }
  };

  const handleDownloadQrCode = async () => {
    if (!qrCodeImageSrc) {
      message.warning('暂无二维码图片');
      return;
    }
    try {
      const response = await fetch(qrCodeImageSrc);
      if (!response.ok) {
        throw new Error('二维码图片下载失败');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${qrChannel?.name || '活动投放'}二维码.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(error);
      message.error('二维码图片下载失败，请检查图片地址是否允许下载');
    }
  };

  const handleCopyQrLink = async () => {
    const link = qrDetail?.qrContentUrl || qrDetail?.shortUrl;
    if (!link) {
      message.warning('暂无可复制链接');
      return;
    }
    try {
      await copyToClipboard(link);
      message.success('复制成功');
    } catch (error) {
      console.error(error);
      message.error('复制失败');
    }
  };

  const handleCopyQrImage = async () => {
    if (!qrCodeImageSrc) {
      message.warning('暂无二维码图片');
      return;
    }
    try {
      const response = await fetch(qrCodeImageSrc);
      const blob = await response.blob();
      const ClipboardItem = (window as any).ClipboardItem;
      if (navigator.clipboard && ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        message.success('图片复制成功');
        return;
      }
      await copyToClipboard(qrCodeImageSrc);
      message.success('已复制图片链接');
    } catch (error) {
      console.error(error);
      try {
        await copyToClipboard(qrCodeImageSrc);
        message.success('已复制图片链接');
      } catch (copyError) {
        console.error(copyError);
        message.error('复制失败');
      }
    }
  };

  const renderCurrentQrType = () => {
    return qrDetail?.expireType === 2 ? '自定义有效期二维码' : '长期有效二维码';
  };

  const renderPreviewQrType = () => {
    return qrExpireType === 2 ? '自定义有效期' : '长期有效';
  };

  const isDeliveryQrDisabled = () => {
    return deliveryLinkActivity?.activityStatus === 'disabled' || deliveryLinkActivity?.runningStatus === 'ended';
  };

  const deliveryLinkColumns = [
    {
      title: '投放渠道',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '渠道投放链接',
      dataIndex: 'url',
      key: 'url',
      render: (url: string) => (
        <Space
          align="center"
          style={{ width: '100%' }}>
          <CopyOutlined
            style={{ flex: 'none' }}
            onClick={() => handleCopyDeliveryLink(url)}
          />
          <Text style={{ whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.5 }}>{url}</Text>
        </Space>
      ),
    },
    {
      title: '二维码',
      key: 'qrCode',
      width: 140,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button
          type="link"
          disabled={isDeliveryQrDisabled()}
          title={isDeliveryQrDisabled() ? '停用或已结束的活动不可查看二维码' : undefined}
          onClick={() => openQrDrawer(record)}>
          查看二维码
        </Button>
      ),
    },
  ];

  const petProfileColumns = [
    {
      title: '活动ID',
      dataIndex: 'activityId',
      key: 'activityId',
    },
    {
      title: '活动名称',
      dataIndex: 'activityName',
      key: 'activityName',
    },
    {
      title: '活动简介',
      dataIndex: 'activityDesc',
      key: 'activityDesc',
    },
    {
      title: '活动开始时间-活动结束时间',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (_: any, record: any) => renderActivityTimeRange(record),
    },
    // {
    //   title: '活动结束时间',
    //   dataIndex: 'endTime',
    //   key: 'endTime',
    // },
    {
      title: '启用状态',
      dataIndex: 'runningStatusName',
      key: 'runningStatusName',
    },
    {
      title: '活动状态',
      dataIndex: 'activityStatusName',
      key: 'activityStatusName',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <>
          <Button
            type="link"
            onClick={() => {
              getDetail(record, 2);
            }}>
            查看
          </Button>
          <Button
            type="link"
            onClick={() => {
              getDetail(record, 3);
            }}>
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => openDeliveryLinkModal('first-archive-gift', record)}>
            活动投放链接
          </Button>
          <Button
            type="link"
            onClick={() => {
              //跳转到TarckData页面
              navigate(`/activityManage/activityManage/activityList/trackData?activityId=${record.activityId}`);
              // window.open(`/activityManage/activityManage/trackData?activityId=${record.activityId}`, '_blank');
            }}>
            数据
          </Button>
        </>
      ),
    },
  ];

  const petProfileV2Columns = [
    {
      title: '活动ID',
      dataIndex: 'activityId',
      key: 'activityId',
    },
    {
      title: '活动名称',
      dataIndex: 'activityName',
      key: 'activityName',
    },
    {
      title: '活动简介',
      dataIndex: 'activityDesc',
      key: 'activityDesc',
    },
    {
      title: '活动开始时间-活动结束时间',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (_: any, record: any) => renderActivityTimeRange(record),
    },
    {
      title: '权益数量',
      dataIndex: 'benefitCount',
      key: 'benefitCount',
    },
    {
      title: '启用状态',
      dataIndex: 'runningStatusName',
      key: 'runningStatusName',
    },
    {
      title: '活动状态',
      dataIndex: 'activityStatusName',
      key: 'activityStatusName',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <>
          <Button
            type="link"
            onClick={() => openPetProfileV2Drawer('view', record)}>
            查看
          </Button>
          <Button
            type="link"
            onClick={() => openPetProfileV2Drawer('edit', record)}>
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => copyPetProfileV2Activity(record)}>
            复制
          </Button>
          <Button
            type="link"
            onClick={() => openDeliveryLinkModal('first-archive-gift-v2', record)}>
            活动投放链接
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDeletePetProfileV2(record)}>
            删除
          </Button>
        </>
      ),
    },
  ];

  const onFinish = async (values: any) => {
    console.log('Success:', values);
    let couponConfig: any = [];
    values.reward.forEach((item: any) => {
      couponConfig.push({ couponId: item });
    });
    let param: any = {
      activityName: values.activityName,
      activityDesc: values.activityDesc,
      activityStatus: values.activityStatus,
      timeType: values.dateType.type,
      activityNotice: editorHtml,
      popupButtonText: values.popupButtonText,
      popupButtonLink: values.popupButtonLink,
      couponConfig,
      platformType: currentShopPlatformType,
      shopId: currentShopId,
      activityType: 'pet_profile',
    };
    if (values.dateType.type == 'specified') {
      param.startTime = values.dateType.time[0].format('YYYY-MM-DD HH:mm:ss');
      param.endTime = values.dateType.time[1].format('YYYY-MM-DD HH:mm:ss');
    }

    if (drawerType === 1) {
      const res = await create(param);
      if (res) {
        message.success('添加成功');
        setOpen(false);
        getList();
      }
    } else {
      param.activityId = editActivityId;
      console.log(param);
      console.log(editActivityId);
      const res = await update(param);
      console.log(res);
      if (res) {
        message.success('编辑成功');
        setOpen(false);
        getList();
      }
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
    message.error(errorInfo.errorFields[0].errors[0]);
  };

  const getCoupon = async () => {
    const { data } = await rewardList({ shopId: currentShopId });
    setCoupon(data as any);
  };

  const getDetail = async (record: any, dt: number) => {
    getCoupon();

    setDrawerType(dt);
    const res: any = await detail({ activityId: record.activityId });
    const { data } = res;

    // 设置新的编辑器内容
    setEditorHtml(data.activityNotice || '');
    setEditActivityId(record.activityId);
    form.setFieldsValue({
      activityId: record.activityId,
      activityName: data.activityName,
      activityDesc: data.activityDesc,
      dateType: {
        drawerType: dt,
        type: data.timeType,
        time:
          data.timeType === 'permanent'
            ? []
            : [dayjs(data.startTime, 'YYYY-MM-DD HH:mm:ss'), dayjs(data.endTime, 'YYYY-MM-DD HH:mm:ss')],
      },
      reward: data.couponConfig.map((item: any) => item.couponId),
      popupButtonText: data.popupButtonText,
      popupButtonLink: data.popupButtonLink,
      activityStatus: data.activityStatus,
    });

    setOpen(true);
  };

  return (
    <div>
      <Card
        title="EC活动"
        style={{ margin: '32px' }}>
        <div>敬请期待</div>
      </Card>
      <Card
        title="店铺活动"
        style={{ margin: '32px' }}>
        <div className={styles.activityCards}>
          {isPetProfileV1Visible ? (
            <Card className={styles.activityCard}>
              <div className={styles['activity']}>
                <div className={styles.activityTitle}>宠物建档礼</div>
                <Button
                  onClick={() => {
                    form.resetFields();
                    if (editor) {
                      editor.setHtml(''); // 清空编辑器实例内容
                    }
                    setDrawerType(1);
                    getCoupon();
                    setOpen(true);
                  }}
                  size="small"
                  type="primary"
                  className={styles.activityButton}>
                  立即创建
                </Button>
              </div>
            </Card>
          ) : null}
          {isPetProfileV2Visible ? (
            <Card className={styles.activityCard}>
              <div className={styles['activity']}>
                <div className={styles.activityTitle}>宠物建档礼V2</div>
                <Button
                  size="small"
                  type="primary"
                  className={styles.activityButton}
                  onClick={() => openPetProfileV2Drawer('create')}>
                  立即创建
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </Card>
      <div className={styles['listTitle']}>
        <div>活动列表</div>
      </div>
      <Tabs
        style={{ margin: '8px 32px 0' }}
        activeKey={activeTab}
        onChange={onChange}
        items={[
          ...(isPetProfileV1Visible
            ? [
                {
                  label: `宠物建档礼`,
                  key: ACTIVITY_TABS.PET_PROFILE,
                },
              ]
            : []),
          ...(isPetProfileV2Visible
            ? [
                {
                  label: `宠物建档礼V2`,
                  key: ACTIVITY_TABS.PET_PROFILE_V2,
                },
              ]
            : []),
        ]}
      />
      <Table
        rowKey={(record: any) => record.activityId}
        style={{ marginLeft: 32, marginRight: 32 }}
        columns={activeTab === ACTIVITY_TABS.PET_PROFILE_V2 ? petProfileV2Columns : petProfileColumns}
        dataSource={data}
        pagination={{
          pageSize: page.pageSize,
          current: page.pageNum,
          total: page.total,
          onChange: (pageNum, pageSize) => {
            console.log(pageNum);
            setPage(prev => ({ ...prev, pageNum, pageSize: pageSize || prev.pageSize }));
          },
        }}
      />

      <Drawer
        destroyOnClose
        title={`${drawerType === 1 ? '新建' : drawerType === 2 ? '查看' : '编辑'}-宠物建档礼`}
        size="large"
        placement="right"
        onClose={onClose}
        open={open}
        extra={
          <div>
            {drawerType !== 2 && (
              <Button
                type="primary"
                onClick={() => {
                  form.submit();
                }}>
                保存
              </Button>
            )}
          </div>
        }>
        <Alert
          message="活动注意事项"
          description={
            <ol>
              <li>该活动只适用于天猫店铺,且需为陆泽直接管理的会员通店铺</li>
              <li>该活动只支持天猫运营后台创建的会员专享券奖励</li>
            </ol>
          }
          type="warning"
          showIcon
        />
        {open && (
          <Form
            name="basic"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            style={{ marginTop: '24px' }}
            form={form}>
            <Form.Item
              label="活动名称"
              name="activityName"
              rules={[{ required: true }]}>
              <Input
                placeholder="请输入活动名称"
                disabled={drawerType === 2 || drawerType === 3}
              />
            </Form.Item>
            <Form.Item
              label="活动简介"
              name="activityDesc"
              rules={[{ required: true }]}>
              <Input
                placeholder="请输入活动简介"
                disabled={drawerType === 2}
              />
            </Form.Item>
            <Form.Item
              label="活动时间"
              name="dateType"
              rules={[{ required: true }]}>
              <DateType></DateType>
            </Form.Item>
            <Form.Item
              label="活动状态"
              name="activityStatus"
              rules={[{ required: true }]}>
              <Radio.Group disabled={drawerType === 2}>
                <Radio value={'disabled'}>停用</Radio>
                <Radio value={'enabled'}>启用</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label="奖励类型"
              rules={[{ required: true }]}>
              <Select
                value={'会员专享券'}
                options={[]}
                disabled
              />
            </Form.Item>
            <Form.Item
              label="奖励选择"
              name="reward"
              rules={[{ required: true }]}>
              <Select
                placeholder="请选择奖励"
                options={coupon}
                mode="multiple"
                fieldNames={{ label: 'couponName', value: 'couponId' }}
                disabled={drawerType === 2}
              />
            </Form.Item>
            <Form.Item
              label={POPUP_BUTTON_TEXT_LABEL}
              name="popupButtonText">
              <Input
                placeholder="请输入"
                disabled={drawerType === 2}
                maxLength={6}
                showCount={INPUT_COUNT_PROPS}
              />
            </Form.Item>
            <Form.Item
              label="弹窗按钮跳转"
              name="popupButtonLink">
              <Input
                placeholder="请输入"
                disabled={drawerType === 2}
              />
            </Form.Item>
            <Form.Item
              label="权益说明"
              required>
              <div className={styles.editorWrap}>
                <Toolbar
                  editor={editor}
                  defaultConfig={toolbarConfig as any}
                  className={styles.toolbar}
                  // style={{ pointerEvents: drawerType === 2 ? 'none' : 'auto' }}
                />
                <Editor
                  defaultConfig={{
                    ...defaultEditorConfig,
                    readOnly: drawerType === 2 || drawerType === 3,
                    maxLength: 1000,
                  }}
                  value={editorHtml}
                  onCreated={editorInstance => {
                    setEditor(editorInstance);
                    setEditorReady(true); // 标记编辑器已准备好
                  }}
                  onChange={(ed: any) => {
                    const html = ed.getHtml();
                    setEditorHtml(html);
                    form.setFieldsValue({ validityDescription: html });
                  }}
                  // style={{ pointerEvents: drawerType === 2 ? 'none' : 'auto' }}
                  className={styles.editorInner}
                />
              </div>
            </Form.Item>
          </Form>
        )}
      </Drawer>
      <PetProfileV2Drawer
        open={v2DrawerOpen}
        mode={v2DrawerMode}
        activityId={v2ActivityId}
        copyFromActivityId={v2CopyFromActivityId}
        shopId={currentShopId}
        platformType={currentShopPlatformType}
        onClose={closePetProfileV2Drawer}
        onSuccess={() => {
          setActiveTab(ACTIVITY_TABS.PET_PROFILE_V2);
          setPage(prev => ({
            ...prev,
            pageNum: 1,
          }));
          setListRefreshVersion(prev => prev + 1);
        }}
      />
      <Modal
        title="活动投放链接"
        open={deliveryLinkModalOpen}
        footer={null}
        width={1100}
        onCancel={() => setDeliveryLinkModalOpen(false)}>
        <Table
          rowKey="code"
          columns={deliveryLinkColumns}
          dataSource={deliveryLinks}
          pagination={false}
        />
      </Modal>
      <Drawer
        destroyOnClose
        title="查看二维码"
        width={720}
        placement="right"
        onClose={closeQrDrawer}
        open={qrDrawerOpen}
        extra={
          <Space>
            <Button onClick={closeQrDrawer}>关闭</Button>
            <Button
              type="primary"
              loading={qrSaving}
              onClick={handleSaveQrConfig}>
              保存配置
            </Button>
          </Space>
        }>
        <Spin spinning={qrDrawerLoading}>
          <Space
            direction="vertical"
            size="large"
            style={{ width: '100%' }}>
            <Descriptions
              bordered
              column={1}
              size="small"
              labelStyle={QR_DESCRIPTION_LABEL_STYLE}>
              <Descriptions.Item label="活动名称">{deliveryLinkActivity?.activityName || '--'}</Descriptions.Item>
              <Descriptions.Item label="投放渠道">{qrChannel?.name || '--'}</Descriptions.Item>
              <Descriptions.Item label="原始链接">
                <Paragraph
                  copyable
                  style={{ marginBottom: 0 }}>
                  {qrChannel?.url || '--'}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <Card
              size="small"
              title="有效期设置">
              <Form
                form={qrForm}
                layout="horizontal"
                labelCol={{ flex: `${QR_LABEL_WIDTH}px` }}
                wrapperCol={{ flex: 'auto' }}
                initialValues={{
                  expireType: 1,
                  expireActionValue: EXPIRE_ACTION_OPTIONS[0].value,
                }}>
                <Form.Item
                  name="expireType"
                  label="有效期类型"
                  rules={[{ required: true }]}>
                  <Radio.Group>
                    <Space
                      direction="vertical"
                      size="small">
                      <Radio value={1}>
                        <Space
                          direction="vertical"
                          size={0}>
                          <span>长期有效</span>
                          <Text type="secondary">二维码承载原投放链接，活动状态由活动页控制。</Text>
                        </Space>
                      </Radio>
                      <Radio value={2}>
                        <Space
                          direction="vertical"
                          size={0}>
                          <span>自定义有效时间</span>
                          <Text type="secondary">二维码承载短链，由服务端校验过期规则。</Text>
                        </Space>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>

                <Form.Item label="当前生效">
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}>
                    <Alert
                      message={renderCurrentQrType()}
                      type="info"
                      showIcon
                    />
                    {qrDetail && qrExpireType !== qrDetail.expireType ? (
                      <Alert
                        message={`同一活动、同一投放渠道仅允许一个生效二维码。确认切换后，当前二维码将按新的${renderPreviewQrType()}规则生效。`}
                        type="warning"
                        showIcon
                      />
                    ) : null}
                  </Space>
                </Form.Item>

                <Form.Item
                  label="有效时间"
                  name="effectiveTime"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (qrExpireType === 2 && (!value || value.length !== 2)) {
                          return Promise.reject(new Error('请选择有效时间'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <RangePicker
                    showTime
                    disabled={qrExpireType !== 2}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD HH:mm:ss"
                  />
                </Form.Item>

                <Form.Item
                  label="过期后处理"
                  name="expireActionValue"
                  rules={[{ required: true }]}
                  extra={
                    qrExpireType === 2
                      ? '过期后用户将看到配置的兜底动作。'
                      : '选择自定义有效时间后，可配置二维码过期后的兜底动作。'
                  }>
                  <Select
                    disabled={qrExpireType !== 2}
                    options={EXPIRE_ACTION_OPTIONS.map(item => ({ label: item.label, value: item.value }))}
                  />
                </Form.Item>
              </Form>
            </Card>

            <Card
              size="small"
              title="二维码预览">
              <Space
                direction="vertical"
                size="middle"
                style={{ width: '100%' }}>
                <Descriptions
                  bordered
                  column={1}
                  size="small"
                  labelStyle={QR_DESCRIPTION_LABEL_STYLE}>
                  <Descriptions.Item label="二维码图片">
                    {qrCodeImageSrc ? (
                      <img
                        src={qrCodeImageSrc}
                        alt={`${qrChannel?.name || ''}二维码`}
                        style={{ width: 180, height: 180, objectFit: 'contain' }}
                      />
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="暂无二维码图片"
                      />
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="二维码名称">{qrChannel?.name || '--'} 二维码</Descriptions.Item>
                  <Descriptions.Item label="有效期类型">
                    <Tag color={qrExpireType === 2 ? 'warning' : 'success'}>{renderPreviewQrType()}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="二维码承载链接（短链）">
                    <Paragraph
                      copyable
                      style={{ marginBottom: 0, wordBreak: 'break-all' }}>
                      {qrDetail?.qrContentUrl || qrDetail?.shortUrl || '--'}
                    </Paragraph>
                  </Descriptions.Item>
                  <Descriptions.Item label="说明">
                    二维码按活动和投放渠道独立管理；即使不同活动链接相同，也不会共用同一个二维码。
                  </Descriptions.Item>
                </Descriptions>
                <Space wrap>
                  <Button
                    type="primary"
                    onClick={handleDownloadQrCode}>
                    下载二维码
                  </Button>
                  <Button onClick={handleCopyQrImage}>复制图片</Button>
                  <Button onClick={handleCopyQrLink}>复制链接</Button>
                </Space>
              </Space>
            </Card>
          </Space>
        </Spin>
      </Drawer>
    </div>
  );
};
export default ActivityList;
